import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { salesService, announcementsService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SecurityPopup from '../components/SecurityPopup';
import '../styles/Dashboard.css';

const OPCOES_HIERARQUIA = [
  { value: 'vendedor', label: 'Vendedores' },
  { value: 'backoffice', label: 'Backoffice (BKO)' },
  { value: 'supervisor_bko', label: 'Supervisor BKO' },
  { value: 'supervisor', label: 'Supervisores' },
  { value: 'diretor', label: 'Diretor' },
  { value: 'admin', label: 'Admin' }
];

// Mês/ano atuais em Brasília (America/Sao_Paulo)
const getCurrentMonthYearBrasilia = () => {
  const now = new Date();
  const brtMs = now.getTime() - 3 * 60 * 60 * 1000;
  const d = new Date(brtMs);
  return { mes: d.getUTCMonth() + 1, ano: d.getUTCFullYear() };
};

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const Dashboard = () => {
  const current = useMemo(getCurrentMonthYearBrasilia, []);
  const [mesAtual, setMesAtual] = useState(current.mes);
  const [anoAtual, setAnoAtual] = useState(current.ano);
  const [canal, setCanal] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anuncios, setAnuncios] = useState([]);
  const [showAnuncioModal, setShowAnuncioModal] = useState(false);
  const [anunciosGerenciar, setAnunciosGerenciar] = useState([]);
  const [anunciosGerenciarLoading, setAnunciosGerenciarLoading] = useState(false);
  const [anuncioLoading, setAnuncioLoading] = useState(false);
  const [anuncioError, setAnuncioError] = useState('');
  const [anuncioSuccess, setAnuncioSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [formAnuncio, setFormAnuncio] = useState({
    titulo: '',
    conteudo: '',
    target_roles: [],
    data_inicio: '',
    data_fim: '',
    imagem: null
  });
  const [imagemPreview, setImagemPreview] = useState(null);
  const { user } = useAuth();
  const [showSecurityPopup, setShowSecurityPopup] = useState(false);

  useEffect(() => {
    const shouldShowPopup = localStorage.getItem('showSecurityPopup');
    if (shouldShowPopup === 'true') {
      setShowSecurityPopup(true);
    }
  }, []);

  useEffect(() => {
    loadStats(mesAtual, anoAtual, canal);
  }, [mesAtual, anoAtual, canal]);

  useEffect(() => {
    loadAnuncios();
  }, []);

  const loadStats = async (mes, ano, canalFiltro) => {
    try {
      setLoading(true);
      const data = await salesService.getStats(mes, ano, canalFiltro || undefined);
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnuncios = async () => {
    try {
      const data = await announcementsService.getVisible();
      setAnuncios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar anúncios:', error);
      setAnuncios([]);
    }
  };

  const loadAnunciosGerenciar = async () => {
    if (user?.role !== 'admin' && user?.role !== 'diretor') return;
    try {
      setAnunciosGerenciarLoading(true);
      const data = await announcementsService.getAll();
      setAnunciosGerenciar(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar anúncios para gerenciar:', error);
      setAnunciosGerenciar([]);
    } finally {
      setAnunciosGerenciarLoading(false);
    }
  };

  const openAnuncioModal = () => {
    setFormAnuncio({
      titulo: '',
      conteudo: '',
      target_roles: [],
      data_inicio: '',
      data_fim: '',
      imagem: null
    });
    setImagemPreview(null);
    setAnuncioError('');
    setAnuncioSuccess('');
    setShowAnuncioModal(true);
    loadAnunciosGerenciar();
  };

  const handleExcluirAnuncio = async (id) => {
    if (!window.confirm('Excluir este anúncio? Quem já viu não será afetado, mas ele deixará de aparecer para todos.')) return;
    try {
      setDeletingId(id);
      await announcementsService.delete(id);
      await loadAnunciosGerenciar();
      await loadAnuncios();
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao excluir anúncio.';
      setAnuncioError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const closeAnuncioModal = () => {
    setShowAnuncioModal(false);
    setAnuncioError('');
    setAnuncioSuccess('');
  };

  const handleAnuncioChange = (field, value) => {
    setFormAnuncio(prev => ({ ...prev, [field]: value }));
  };

  const handleImagemChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setAnuncioError('Imagem deve ter no máximo 5MB.');
        return;
      }
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        setAnuncioError('Formato de imagem não suportado. Use JPEG, PNG, GIF ou WEBP.');
        return;
      }
      setFormAnuncio(prev => ({ ...prev, imagem: file }));
      setImagemPreview(URL.createObjectURL(file));
      setAnuncioError('');
    }
  };

  const removeImagem = () => {
    setFormAnuncio(prev => ({ ...prev, imagem: null }));
    setImagemPreview(null);
  };

  const toggleTargetRole = (role) => {
    setFormAnuncio(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const handleAnuncioSubmit = async (e) => {
    e.preventDefault();
    setAnuncioError('');
    setAnuncioSuccess('');
    
    // Verificar se pelo menos imagem ou texto foi fornecido
    const temImagem = formAnuncio.imagem !== null;
    const temTexto = formAnuncio.titulo.trim() || formAnuncio.conteudo.trim();
    
    if (!temImagem && !temTexto) {
      setAnuncioError('Forneça pelo menos uma imagem ou título/conteúdo.');
      return;
    }
    
    if (!formAnuncio.target_roles.length) {
      setAnuncioError('Selecione ao menos um público (hierarquia).');
      return;
    }
    if (!formAnuncio.data_inicio || !formAnuncio.data_fim) {
      setAnuncioError('Data de início e data de fim são obrigatórias.');
      return;
    }
    if (formAnuncio.data_fim < formAnuncio.data_inicio) {
      setAnuncioError('Data de fim deve ser igual ou posterior à data de início.');
      return;
    }
    try {
      setAnuncioLoading(true);
      await announcementsService.create({
        titulo: formAnuncio.titulo.trim(),
        conteudo: formAnuncio.conteudo.trim(),
        target_roles: formAnuncio.target_roles,
        data_inicio: formAnuncio.data_inicio,
        data_fim: formAnuncio.data_fim,
        imagem: formAnuncio.imagem
      });
      setAnuncioSuccess('Anúncio publicado com sucesso.');
      await loadAnuncios();
      setTimeout(() => {
        closeAnuncioModal();
      }, 1200);
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Erro ao publicar anúncio.';
      setAnuncioError(msg);
    } finally {
      setAnuncioLoading(false);
    }
  };

  // Últimos 24 meses para o select (valor "mes|ano")
  const opcoesMeses = useMemo(() => {
    const opts = [];
    const { mes: m0, ano: a0 } = getCurrentMonthYearBrasilia();
    for (let i = 0; i < 24; i++) {
      let mes = m0 - i;
      let ano = a0;
      while (mes <= 0) { mes += 12; ano -= 1; }
      opts.push({ mes, ano, label: `${MESES[mes - 1]} ${ano}` });
    }
    return opts;
  }, []);

  const showCanalFilter = user && ['admin', 'diretor', 'vendedor', 'supervisor'].includes(user.role);

  if (loading) {
    return (
      <Layout>
        <div className="loading">Carregando...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      {showSecurityPopup && (
        <SecurityPopup
          user={user}
          onContinue={() => {
            setShowSecurityPopup(false);
            localStorage.removeItem('showSecurityPopup');
          }}
        />
      )}
      <div className="dashboard">
        <div className="dashboard-header">
          <div>
            <h1>Dashboard</h1>
            <p>Bem-vindo(a), {user?.nome}!</p>
          </div>
          <div className="dashboard-filters">
            <div className="dashboard-month-filter">
              <label htmlFor="dashboard-mes">Mês:</label>
              <select
                id="dashboard-mes"
                value={`${mesAtual}|${anoAtual}`}
                onChange={(e) => {
                  const [m, a] = e.target.value.split('|').map(Number);
                  setMesAtual(m);
                  setAnoAtual(a);
                }}
                className="dashboard-filter-select"
              >
                {opcoesMeses.map(({ mes, ano, label }) => (
                  <option key={`${mes}-${ano}`} value={`${mes}|${ano}`}>{label}</option>
                ))}
              </select>
            </div>
            {showCanalFilter && (
              <div className="dashboard-month-filter">
                <label htmlFor="dashboard-canal">Canal:</label>
                <select
                  id="dashboard-canal"
                  value={canal}
                  onChange={(e) => setCanal(e.target.value)}
                  className="dashboard-filter-select"
                >
                  <option value="">Todos</option>
                  <option value="extk">EXTK</option>
                  <option value="onda">ONDA</option>
                  <option value="vision">VISION</option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card total">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>Total de Vendas</h3>
              <p className="stat-value">{stats?.totalVendas || 0}</p>
            </div>
          </div>

          <div className="stat-card pendente">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>Pendentes</h3>
              <p className="stat-value">{stats?.vendasPendentes || 0}</p>
            </div>
          </div>

          <div className="stat-card tratamento">
            <div className="stat-icon">🔄</div>
            <div className="stat-content">
              <h3>Em Tratamento</h3>
              <p className="stat-value">{stats?.vendasEmTratamento || 0}</p>
            </div>
          </div>

          <div className="stat-card concluida">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>Concluídas</h3>
              <p className="stat-value">{stats?.vendasConcluidas || 0}</p>
            </div>
          </div>

          <div className="stat-card cancelada">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <h3>Canceladas</h3>
              <p className="stat-value">{stats?.vendasCanceladas || 0}</p>
            </div>
          </div>

          {(user?.role !== 'backoffice' && user?.role !== 'supervisor_bko') && (
            <div className="stat-card valor">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>Valor Total (Concluídas)</h3>
                <p className="stat-value">
                  R$ {(stats?.valorTotal || 0).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

        {anuncios.length > 0 && (
          <div className="dashboard-anuncios">
            <h2>Informativos</h2>
            <div className="anuncios-list">
              {anuncios.map((a) => (
                <div key={a.id} className={`anuncio-card ${a.imagem_url ? 'with-image' : ''}`}>
                  {a.imagem_url && (
                    <div className="anuncio-imagem-wrapper">
                      <img
                        src={announcementsService.getImageUrl(a.imagem_url)}
                        alt={a.titulo}
                        className="anuncio-imagem"
                      />
                    </div>
                  )}
                  <div className="anuncio-content">
                    <h3 className="anuncio-titulo">{a.titulo}</h3>
                    <p className="anuncio-conteudo">{a.conteudo}</p>
                    <span className="anuncio-periodo">
                      De {new Date(a.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR')} até {new Date(a.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="dashboard-actions">
          <h2>Ações Rápidas</h2>
          <div className="actions-grid">
            {user && (user.role === 'admin' || user.role === 'diretor') && (
              <button type="button" className="action-card action-card-button" onClick={openAnuncioModal}>
                <div className="action-icon">📢</div>
                <h3>Anúncios</h3>
                <p>Publicar anúncio ou informativo para vendedores ou BKO</p>
              </button>
            )}
            {user && user.role !== 'backoffice' && user.role !== 'supervisor_bko' && (
              <a href="/vendas/consulta-viabilidade" className="action-card">
                <div className="action-icon">➕</div>
                <h3>Nova Venda</h3>
                <p>Cadastrar uma nova venda no sistema</p>
              </a>
            )}

            <a href="/vendas" className="action-card">
              <div className="action-icon">📋</div>
              <h3>Ver Vendas</h3>
              <p>Visualizar todas as vendas cadastradas</p>
            </a>

            {user && (['admin', 'diretor'].includes(user.role) || user.role === 'backoffice' || user.role === 'supervisor_bko') && (
              <a href="/fila-tratamento" className="action-card">
                <div className="action-icon">🔧</div>
                <h3>Fila de Tratamento</h3>
                <p>Gerenciar vendas pendentes de tratamento</p>
              </a>
            )}

            {user && ['admin', 'diretor', 'supervisor', 'vendedor'].includes(user.role) && (
              <a href="/vendas-pagamento" className="action-card">
                <div className="action-icon">💰</div>
                <h3>Controle de Pagamentos</h3>
                <p>{(user.role === 'vendedor' || user.role === 'supervisor') ? 'Visualize seus pagamentos' : 'Gerenciar status de pagamento das vendas'}</p>
              </a>
            )}

            {user && ['admin', 'diretor'].includes(user.role) && (
              <a href="/usuarios" className="action-card">
                <div className="action-icon">👥</div>
                <h3>Gerenciar Usuários</h3>
                <p>Gerenciar usuários do sistema</p>
              </a>
            )}

            {user && user.role === 'admin' && (
              <a href="/config-api" className="action-card">
                <div className="action-icon">⚙️</div>
                <h3>Configuração API</h3>
                <p>Configurar credenciais da API de consulta</p>
              </a>
            )}

            {user && user.role === 'admin' && (
              <a href="/whatsapp" className="action-card">
                <div className="action-icon">📱</div>
                <h3>WhatsApp</h3>
                <p>Gerenciar conexao e mensagens do WhatsApp</p>
              </a>
            )}
          </div>
        </div>

        {showAnuncioModal && (
          <div className="modal-overlay" onClick={closeAnuncioModal}>
            <div className="modal-content modal-content-anuncio modal-content-anuncio-gerenciar" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Anúncios / informativos</h2>
                <span className="close-modal" onClick={closeAnuncioModal}>×</span>
              </div>
              <div className="modal-body">
                <section className="anuncios-gerenciar">
                  <h3>Anúncios publicados</h3>
                  {anunciosGerenciarLoading ? (
                    <p className="anuncios-gerenciar-loading">Carregando...</p>
                  ) : anunciosGerenciar.length === 0 ? (
                    <p className="anuncios-gerenciar-empty">Nenhum anúncio publicado ainda.</p>
                  ) : (
                    <ul className="anuncios-gerenciar-list">
                      {anunciosGerenciar.map((a) => (
                        <li key={a.id} className="anuncios-gerenciar-item">
                          <div className="anuncios-gerenciar-item-content">
                            <strong>{a.titulo}</strong>
                            <span className="anuncios-gerenciar-item-periodo">
                              {new Date(a.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR')} até {new Date(a.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}
                            </span>
                            <span className="anuncios-gerenciar-item-roles">
                              Público: {Array.isArray(a.target_roles) ? a.target_roles.join(', ') : '-'}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn-excluir-anuncio"
                            onClick={() => handleExcluirAnuncio(a.id)}
                            disabled={deletingId === a.id}
                            title="Excluir anúncio"
                          >
                            {deletingId === a.id ? 'Excluindo...' : 'Excluir'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <hr className="modal-divider" />

                <h3>Novo anúncio</h3>
                <form onSubmit={handleAnuncioSubmit}>
                  {anuncioError && <p className="modal-error">{anuncioError}</p>}
                  {anuncioSuccess && <p className="modal-success">{anuncioSuccess}</p>}
                  <div className="form-group">
                    <label htmlFor="anuncio-titulo">Título</label>
                    <input
                      id="anuncio-titulo"
                      type="text"
                      value={formAnuncio.titulo}
                      onChange={(e) => handleAnuncioChange('titulo', e.target.value)}
                      placeholder="Título do anúncio"
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="anuncio-conteudo">Conteúdo</label>
                    <textarea
                      id="anuncio-conteudo"
                      value={formAnuncio.conteudo}
                      onChange={(e) => handleAnuncioChange('conteudo', e.target.value)}
                      placeholder="Texto do informativo"
                      rows={5}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="anuncio-imagem">Imagem (opcional)</label>
                    <div className="anuncio-imagem-upload">
                      <input
                        id="anuncio-imagem"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImagemChange}
                        className="anuncio-imagem-input"
                      />
                      {imagemPreview && (
                        <div className="anuncio-imagem-preview">
                          <img src={imagemPreview} alt="Preview" />
                          <button type="button" className="btn-remove-imagem" onClick={removeImagem}>
                            Remover
                          </button>
                        </div>
                      )}
                      <span className="anuncio-imagem-hint">Formatos aceitos: JPEG, PNG, GIF, WEBP. Máx: 5MB</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Mostrar para (hierarquia)</label>
                    <div className="anuncio-target-roles">
                      {OPCOES_HIERARQUIA.map((op) => (
                        <label key={op.value} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formAnuncio.target_roles.includes(op.value)}
                            onChange={() => toggleTargetRole(op.value)}
                          />
                          <span>{op.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="anuncio-data-inicio">Data início</label>
                      <input
                        id="anuncio-data-inicio"
                        type="date"
                        value={formAnuncio.data_inicio}
                        onChange={(e) => handleAnuncioChange('data_inicio', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="anuncio-data-fim">Data fim</label>
                      <input
                        id="anuncio-data-fim"
                        type="date"
                        value={formAnuncio.data_fim}
                        onChange={(e) => handleAnuncioChange('data_fim', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={closeAnuncioModal}>
                      Fechar
                    </button>
                    <button type="submit" className="btn-primary" disabled={anuncioLoading}>
                      {anuncioLoading ? 'Publicando...' : 'Publicar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;